import React from 'react';
import { ApolloProvider, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import client from '../../lib/apolloClient';
import BuyerRoom from '../../components/Succession/BuyerRoom/BuyerRoom';
import getMessages from '../../lib/i18n/getMessages';
import { CurrencyProvider } from '../../hooks/useCurrency';

// Simple, clean GraphQL query that gets everything we need in one go
const GET_BUYER_ROOM_DATA = gql`
  query GetBuyerRoomData($dealName: String!) {
    getCurrentUser {
      id
      name
      email
      motivation
      profile_picture
    }
    
    getBuyerRoomData(dealName: $dealName) {
      id
      deal_name
      headline
      sub_headline
      summary_of_business
      ideal_candidate
      list_price
      deal_type
      deal_state
      closed
      approval_status
      customer_demographics_comments
      products_and_services_comments
      growth_opportunities
      assets_included
      intangible_assets_included
      ideal_candidate
      inventory_user {
        id
        user {
          id
          allowed_name
          city
          province
          motivation
          fb_user_name
          # Client demographics
          avg_client_age
          number_of_clients
          niche_market
          assets_under_management
          # Financial data
          annual_revenue
          recurring_revenue
          year_1_gross
          year_2_gross
          year_3_gross
          year_4_gross
          years_practicing
          fee_based_revenue
          trailer_revenue
          direct_revenue
          transactional_revenue
          # Product mix
          tag_list
          percent_fee_based
          percent_commission
          percent_non_recurring_revenue
          percent_revenue_recurring
          
        }
      }
    }
    
    getUserDealInterestForDeal(dealName: $dealName) {
      id
      workflow_state
      # Partial Sales: the Partial Sale this invite came from (null otherwise).
      partial_sale_listing { id label closed }
      invited
      shortlisted
      saved
      proposal_letter
      receiving_party_nda_sent_timestamp
      disclosing_party_nda_sent_timestamp
      a1
      a2
      a3
      a4
      a5
      inventory_user {
        id
        user {
          id
          name
          conversation_with_me {
            id
          }
        }
      }
      documents {
        id
        name
        category
        download_url
        uploaded_by {
          name
        }
        created_at
        document_type
      }
      cash_flows {
        id
        name
        purchasePrice
        buyerLumpSumAmount
        buyerHasFinancing
        buyerHasFixedPayments
        buyerHasContinuingPayments
        buyer1AverageCashFlow
        sellerAverageCashFlow
        friendlyName
        illustration {
          sellerCashFlow {
            freeCashFlow
          }
          buyer1CashFlow {
            freeCashFlow
          }
        }
      }
    }
    
    company {
      id
      name
      host
      deal_room_branded_name
      deal_room_branded_name_fr
      deal_room_single_word
      deal_room_single_word_fr
      stylesheet_name
      hide_list_price_in_deal_room_wizard_succession
      hide_list_price_in_deal_room_wizard_sale
      hide_list_price_in_deal_room_wizard_continuity
    }
  }
`;

const BuyerRoomWithData = ({ dealName, isAuthenticated }) => {
  const locale = window.locale || 'en';
  const messages = getMessages(locale);
  const t = (id, fallback) => messages[id] || fallback;

  // Use useQuery hook — must be called before any conditional returns
  const { loading, error, data } = useQuery(GET_BUYER_ROOM_DATA, {
    variables: { dealName },
    skip: !dealName,
  });

  if (!dealName) {
    return (
      <IntlProvider locale={locale} messages={messages}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900">{t('succession_deals.wizard.steps.buyer_room.errors.deal_not_found_title', 'Deal Not Found')}</h3>
            <p className="text-sm text-gray-500">{t('succession_deals.wizard.steps.buyer_room.errors.deal_not_found_description', 'No deal specified.')}</p>
            <button
              onClick={() => window.location.href = '/succession/deals'}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('deal_page.back_to_deals', 'Back to Deals')}
            </button>
          </div>
        </div>
      </IntlProvider>
    );
  }

  // Handle loading state
  if (loading) {
    return (
      <IntlProvider locale={locale} messages={messages}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('succession_deals.wizard.steps.buyer_room.loading', 'Loading buyer room...')}</h3>
          </div>
        </div>
      </IntlProvider>
    );
  }

  // Handle error state
  if (error) {
    console.error('GraphQL Error in BuyerRoomGraphQLContainer:', error);
    return (
      <IntlProvider locale={locale} messages={messages}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">{t('succession_deals.wizard.steps.buyer_room.error_boundary.title', 'Error Loading Buyer Room')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {error.message || t('succession_deals.wizard.steps.buyer_room.error_boundary.description', 'There was an error loading the buyer room data.')}
            </p>
            <button
              onClick={() => window.location.href = '/succession/deals'}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('deal_page.back_to_deals', 'Back to Deals')}
            </button>
          </div>
        </div>
      </IntlProvider>
    );
  }

  // Much simpler - no complex searching or null checking needed
  const deal = data?.getBuyerRoomData;
  const dealInterest = data?.getUserDealInterestForDeal;
  const currentUser = data?.getCurrentUser;
  const company = data?.company;

  // Handle not found state
  if (!deal || !dealInterest) {
    return (
      <IntlProvider locale={locale} messages={messages}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">{t('succession_deals.wizard.steps.buyer_room.errors.deal_not_found_title', 'Deal Not Found')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('succession_deals.wizard.steps.buyer_room.errors.access_denied_description', "You don't have access to this deal or it doesn't exist.")}
            </p>
            <button
              onClick={() => window.location.href = '/succession/deals'}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('deal_page.back_to_deals', 'Back to Deals')}
            </button>
          </div>
        </div>
      </IntlProvider>
    );
  }

  // Clean, simple render
  const cashFlows = dealInterest?.cash_flows || [];
  
  return (
    <IntlProvider locale={locale} messages={messages}>
      <BuyerRoom
        currentUser={currentUser}
        company={company}
        deal={deal}
        dealInterest={dealInterest}
        dealName={dealName}
        loading={false}
        cashFlows={cashFlows}
      />
    </IntlProvider>
  );
};

const BuyerRoomGraphQLContainer = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';
  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || window.currency || 'USD'}>
      <ApolloProvider client={client}>
        <BuyerRoomWithData {...props} />
      </ApolloProvider>
    </CurrencyProvider>
  );
};

BuyerRoomGraphQLContainer.propTypes = {
  dealName: PropTypes.string.isRequired,
  isAuthenticated: PropTypes.bool
};

export default BuyerRoomGraphQLContainer; 